import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';

// Every request shows the spinner, so a fast one is held open long enough to register as
// deliberate feedback rather than a flicker.
const MIN_BUSY_MS = 300;

interface IBusyControls {
    begin: () => void;
    end: () => void;
}

const noControls: IBusyControls = { begin: () => {}, end: () => {} };

const BusyStateContext = createContext(false);
const BusyControlsContext = createContext<IBusyControls>(noControls);

/**
 * Tracks how many operations are outstanding so the app is busy until the last one
 * finishes. Ref-counting matters because a page can load its model while a prior
 * submit is still in flight; a plain boolean would clear the overlay too early.
 *
 * The last `end` holds busy open until `MIN_BUSY_MS` has passed since the first `begin`.
 */
export const BusyProvider = ({ children }: { children: ReactNode }) => {
    const outstanding = useRef(0);
    const busySince = useRef(0);
    const release = useRef(0);
    const [isBusy, setIsBusy] = useState(false);

    const begin = useCallback(() => {
        outstanding.current += 1;

        if (outstanding.current > 1) return;

        // Work starting inside the hold supersedes it and restarts the minimum.
        window.clearTimeout(release.current);
        busySince.current = Date.now();

        setIsBusy(true);
    }, []);

    const end = useCallback(() => {
        outstanding.current -= 1;

        if (outstanding.current > 0) return;

        const remaining = MIN_BUSY_MS - (Date.now() - busySince.current);

        if (remaining <= 0) {
            setIsBusy(false);

            return;
        }

        release.current = window.setTimeout(() => setIsBusy(false), remaining);
    }, []);

    const controls = useMemo(() => ({ begin, end }), [begin, end]);

    return (
        <BusyControlsContext.Provider value={controls}>
            <BusyStateContext.Provider value={isBusy}>{children}</BusyStateContext.Provider>
        </BusyControlsContext.Provider>
    );
};

export const useBusy = (): boolean => useContext(BusyStateContext);

/**
 * Holds the app busy for as long as `active` is true, releasing on unmount. Feed it any
 * flag an api hook already exposes, e.g. `useBusyWhile(isSubmitting)`.
 */
export const useBusyWhile = (active: boolean): void => {
    const { begin, end } = useContext(BusyControlsContext);

    useEffect(() => {
        if (!active) return;

        begin();

        return end;
    }, [active, begin, end]);
};
